// frontend/src/pages/reports/BalanceSheet.jsx
import React, { useEffect, useState, useMemo, useCallback } from "react";
import axios from "axios";
import { useAuth } from "../../contexts/AuthContext";
import { useCurrency } from "../../pages/CurrencyContext";
import "./BalanceSheet.css";

const url = process.env.REACT_APP_BACKEND_URL;

const getAuthHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const getCompanyCurrency = (company) => {
  if (!company) return "USD";
  let currency = null;
  if (company.currency) {
    if (typeof company.currency === "object" && company.currency.code) currency = company.currency.code;
    else if (typeof company.currency === "string" && /^[A-Z]{3}$/.test(company.currency)) currency = company.currency;
  }
  if (!currency && company.currency_code) {
    if (typeof company.currency_code === "string" && /^[A-Z]{3}$/.test(company.currency_code)) currency = company.currency_code;
    else if (typeof company.currency_code === "object" && company.currency_code.code) currency = company.currency_code.code;
  }
  if (!currency && company.base_currency) {
    if (typeof company.base_currency === "string" && /^[A-Z]{3}$/.test(company.base_currency)) currency = company.base_currency;
    else if (typeof company.base_currency === "object" && company.base_currency.code) currency = company.base_currency.code;
  }
  if (typeof currency !== "string" || !/^[A-Z]{3}$/.test(currency)) currency = "USD";
  return currency;
};

export default function BalanceSheet() {
  const { company: authCompany } = useAuth();
  const { formatCurrency } = useCurrency();

  // ✅ NEW: full company (with address, contact_2, contact_3, email)
  const [fullCompany, setFullCompany] = useState(null);

  // ✅ NEW: load the full company once
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${url}/api/company/current`, {
          headers: getAuthHeaders(),
          timeout: 5000,
        });
        if (!cancelled && res?.data) setFullCompany(res.data);
      } catch {
        if (!cancelled) setFullCompany(authCompany || null);
      }
    })();
    return () => { cancelled = true; };
  }, [authCompany]);

  // ✅ Use full company, falling back to auth company while loading
  const company = fullCompany || authCompany;

  const companyCurrency = useMemo(() => getCompanyCurrency(company), [company]);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [asOfDate, setAsOfDate] = useState(new Date().toISOString().slice(0, 10));

  const formatAmount = useCallback(
    (amount) => {
      const v = Number(amount || 0);
      try {
        return new Intl.NumberFormat(undefined, {
          style: "currency",
          currency: companyCurrency,
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(v);
      } catch {
        return `${companyCurrency} ${v.toFixed(2)}`;
      }
    },
    [companyCurrency]
  );

  const signedAmount = useCallback(
    (amount) => {
      const v = Number(amount || 0);
      if (v < 0) return `(${formatAmount(Math.abs(v))})`;
      return formatAmount(v);
    },
    [formatAmount]
  );

  const loadBalanceSheet = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${url}/api/reports/balance-sheet`, {
        headers: getAuthHeaders(),
        params: { as_of_date: asOfDate },
      });
      setData(res.data.data || res.data);
    } catch (error) {
      // silent
    } finally {
      setLoading(false);
    }
  }, [asOfDate]);

  useEffect(() => {
    loadBalanceSheet();
  }, [loadBalanceSheet]);

  const handlePrint = () => window.print();

  const currentAssets = data?.assets?.current || [];
  const nonCurrentAssets = data?.assets?.non_current || [];
  const currentLiabilities = data?.liabilities?.current || [];
  const nonCurrentLiabilities = data?.liabilities?.non_current || [];
  const equityAccounts = data?.equity?.accounts || [];
  const supplierAdvances = data?.assets?.supplier_advances || [];

  const currentProfitLoss = Number(data?.equity?.current_profit_loss || 0);
  const totalAssets = Number(data?.assets?.total_assets || 0);
  const totalLiabilities = Number(data?.liabilities?.total_liabilities || 0);
  const totalEquity = Number(data?.equity?.total_equity || 0);
  const liabilitiesAndEquity = totalLiabilities + totalEquity;
  const balanced = Math.abs(totalAssets - liabilitiesAndEquity) < 0.01;
  const difference = Math.abs(totalAssets - liabilitiesAndEquity);

  const displayDate = data?.as_of_date
    ? new Date(data.as_of_date).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })
    : new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

  const fiscalYearLabel = company?.fiscal_year_start
    ? new Date(company.fiscal_year_start).getFullYear()
    : new Date().getFullYear();

  const logoUrl = company?.logo
    ? company.logo.startsWith("http")
      ? company.logo
      : `${url}/${company.logo.replace(/^\/+/, "")}`
    : null;

  // ✅ Address + Phone 1 · 2 · 3 (from /api/company/current)
  const companyAddress = company?.address || "";
  const companyContact = company?.contact || "";
  const companyContact2 = company?.contact_2 || "";
  const companyContact3 = company?.contact_3 || "";
  const companyEmail = company?.admin_email || "";
  const phoneLine = [companyContact, companyContact2, companyContact3]
    .filter(Boolean)
    .join(" · ");

  const sectionTotal = (section) =>
    (section || []).reduce((sum, item) => sum + Number(item.amount || 0), 0);

  const renderRow = (item, idx) => (
    <div className="bs-row" key={item.account_id || `${item.account_name}-${idx}`}>
      <span className="bs-row-name">{item.account_name}</span>
      <span
        className="bs-row-amount"
        style={{
          color: Number(item.amount) < 0 ? "#b91c1c" : "#0f172a",
          fontWeight: Number(item.amount) < 0 ? 600 : 500,
        }}
      >
        {signedAmount(item.amount)}
      </span>
    </div>
  );

  return (
    <div className="balance-sheet-page">
      {/* ================= SCREEN ================= */}
      <div className="screen-only bs-container">
        {/* Title block */}
        <div className="bs-title-block">
          <h1 className="bs-title-main">BALANCE SHEET</h1>
          <div className="bs-title-company">
            {company?.company_name || "Company Name"}
          </div>
          <div className="bs-title-asof">As of {displayDate}</div>
        </div>

        {/* Filter bar */}
        <div className="bs-header">
          <div className="bs-header-left">
            <p className="bs-eyebrow">FINANCIAL REPORTS</p>
            <h2 className="bs-page-title">Balance Sheet</h2>
            <p className="bs-page-sub">
              Assets, liabilities, and equity as of the selected date.
            </p>
          </div>

          <div
            className="bs-actions"
            style={{ display: "flex", gap: "10px", alignItems: "flex-end", flexWrap: "wrap" }}
          >
            <div className="filter-group">
              <label htmlFor="bs-date" style={{ fontSize: 12, display: "block", marginBottom: 4, color: "#170f48", fontWeight: 600 }}>
                As of Date
              </label>
              <input
                id="bs-date"
                type="date"
                value={asOfDate}
                onChange={(e) => setAsOfDate(e.target.value)}
                style={{
                  padding: "10px 12px",
                  border: "1px solid #d9d9d9",
                  borderRadius: 8,
                  fontSize: 14,
                  color: "#170f48",
                  minWidth: 160,
                }}
              />
            </div>

            <button
              onClick={loadBalanceSheet}
              disabled={loading}
              style={{
                padding: "10px 18px",
                background: "#170f48",
                color: "#fff",
                border: "none",
                borderRadius: 8,
                fontSize: 14,
                fontWeight: 600,
                cursor: loading ? "not-allowed" : "pointer",
                minWidth: 120,
                opacity: loading ? 0.6 : 1,
              }}
            >
              ⟳ {loading ? "Refreshing..." : "Refresh"}
            </button>

            <button
              onClick={handlePrint}
              style={{
                padding: "10px 18px",
                background: "#f1592a",
                color: "#fff",
                border: "none",
                borderRadius: 8,
                fontSize: 14,
                fontWeight: 700,
                cursor: "pointer",
                minWidth: 140,
              }}
            >
              🖨 Print (A4)
            </button>
          </div>
        </div>

        {/* Summary cards */}
        <div className="bs-summary">
          <div className="summary-card assets">
            <span>Total Assets</span>
            <strong>{formatAmount(totalAssets)}</strong>
          </div>
          <div className="summary-card liabilities">
            <span>Total Liabilities</span>
            <strong>{formatAmount(totalLiabilities)}</strong>
          </div>
          <div className="summary-card equity">
            <span>Total Equity</span>
            <strong>{formatAmount(totalEquity)}</strong>
          </div>
          <div className="summary-card pnl">
            <span>Current Period P&amp;L</span>
            <strong style={{ color: currentProfitLoss >= 0 ? "#15803d" : "#b91c1c" }}>
              {signedAmount(currentProfitLoss)}
            </strong>
          </div>
        </div>

        {/* Main grid */}
        {loading ? (
          <div className="loading-state">Loading balance sheet...</div>
        ) : (
          <div className="bs-grid">
            {/* LEFT — Assets */}
            <div className="bs-column">
              <div className="bs-column-header">
                <span>ASSETS</span>
                <span>Amount (DR)</span>
              </div>

              <div className="bs-card">
                <div className="bs-group">
                  <div className="bs-group-title">1. Current Assets</div>
                  {currentAssets.length === 0 ? (
                    <div className="bs-empty-row">No current assets</div>
                  ) : (
                    currentAssets.map((item, idx) => renderRow(item, idx))
                  )}
                  <div className="bs-group-total">
                    <span>Total Current Assets</span>
                    <span>{formatAmount(sectionTotal(currentAssets))}</span>
                  </div>
                </div>

                <div className="bs-group">
                  <div className="bs-group-title">2. Non-Current Assets</div>
                  {nonCurrentAssets.length === 0 ? (
                    <div className="bs-empty-row">No non-current assets</div>
                  ) : (
                    nonCurrentAssets.map((item, idx) => renderRow(item, idx))
                  )}
                  <div className="bs-group-total">
                    <span>Total Non-Current Assets</span>
                    <span>{formatAmount(sectionTotal(nonCurrentAssets))}</span>
                  </div>
                </div>

                {supplierAdvances.length > 0 && (
                  <div className="bs-group">
                    <div className="bs-group-title">Other Current Assets</div>
                    <div className="bs-subgroup-title">Supplier Advances</div>
                    {supplierAdvances.map((item, idx) => renderRow(item, idx))}
                    <div className="bs-group-total">
                      <span>Total Other Current Assets</span>
                      <span>{formatAmount(sectionTotal(supplierAdvances))}</span>
                    </div>
                  </div>
                )}

                <div className="bs-card-total">
                  <span>TOTAL ASSETS</span>
                  <span>{formatAmount(totalAssets)}</span>
                </div>
              </div>
            </div>

            {/* RIGHT — L+E */}
            <div className="bs-column">
              <div className="bs-column-header">
                <span>LIABILITIES AND EQUITY</span>
                <span>Amount (CR)</span>
              </div>

              <div className="bs-card">
                <div className="bs-group">
                  <div className="bs-group-title">1. Current Liabilities</div>
                  {currentLiabilities.length === 0 ? (
                    <div className="bs-empty-row">No current liabilities</div>
                  ) : (
                    currentLiabilities.map((item, idx) => renderRow(item, idx))
                  )}
                  <div className="bs-group-total">
                    <span>Total Current Liabilities</span>
                    <span>{formatAmount(sectionTotal(currentLiabilities))}</span>
                  </div>
                </div>

                <div className="bs-group">
                  <div className="bs-group-title">2. Non-Current Liabilities</div>
                  {nonCurrentLiabilities.length === 0 ? (
                    <div className="bs-empty-row">No non-current liabilities</div>
                  ) : (
                    nonCurrentLiabilities.map((item, idx) => renderRow(item, idx))
                  )}
                  <div className="bs-group-total">
                    <span>Total Non-Current Liabilities</span>
                    <span>{formatAmount(sectionTotal(nonCurrentLiabilities))}</span>
                  </div>
                </div>

                <div className="bs-group">
                  <div className="bs-group-title">3. Equity</div>
                  {equityAccounts.length === 0 ? (
                    <div className="bs-empty-row">No equity accounts</div>
                  ) : (
                    equityAccounts.map((item, idx) => renderRow(item, idx))
                  )}

                  <div className="bs-row bs-row-highlight">
                    <span>Current Period Profit / (Loss)</span>
                    <span
                      style={{
                        color: currentProfitLoss >= 0 ? "#15803d" : "#b91c1c",
                        fontWeight: 700,
                      }}
                    >
                      {signedAmount(currentProfitLoss)}
                    </span>
                  </div>

                  <div className="bs-group-total">
                    <span>Total Equity</span>
                    <span>{formatAmount(totalEquity)}</span>
                  </div>
                </div>

                <div className="bs-card-total">
                  <span>TOTAL LIABILITIES &amp; EQUITY</span>
                  <span>{formatAmount(liabilitiesAndEquity)}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Balance check */}
        {!loading && (
          <div className={`bs-check ${balanced ? "balanced" : "unbalanced"}`}>
            <div className="bs-check-icon">{balanced ? "✓" : "!"}</div>
            <div className="bs-check-body">
              <strong className="bs-check-title">
                {balanced
                  ? "Balance Check: Assets = Liabilities + Equity"
                  : "Balance Sheet is Out of Balance"}
              </strong>
              <div className="bs-check-formula">
                <span>{formatAmount(totalAssets)}</span>
                <span className="bs-check-eq">=</span>
                <span>{formatAmount(totalLiabilities)}</span>
                <span className="bs-check-eq">+</span>
                <span>{formatAmount(totalEquity)}</span>

                {balanced ? (
                  <span className="bs-check-badge bs-check-badge-ok">✓ Balanced</span>
                ) : (
                  <span className="bs-check-badge bs-check-badge-bad">
                    Difference: {formatAmount(difference)}
                  </span>
                )}
              </div>

              {!balanced && difference > 0 && (
                <p style={{ margin: "10px 0 0", fontSize: 12.5, color: "#b91c1c", lineHeight: 1.5 }}>
                  <strong>Tip:</strong> This is often caused by legacy single-sided
                  expense entries that were posted before the double-entry
                  journal system. Run the one-time backfill route{" "}
                  <code style={{ background: "#fee2e2", padding: "1px 6px", borderRadius: 4 }}>
                    POST /api/reports/backfill-expenses
                  </code>{" "}
                  to repair them automatically.
                </p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ================= PRINT ================= */}
      <div className="print-only print-page">
        <div className="print-header">
          <div className="print-header-left">
            {logoUrl && <img src={logoUrl} alt="Logo" className="print-logo" />}
            <div className="print-header-text">
              <h1 className="print-company-name">
                {company?.company_name || "Company Name"}
              </h1>

              {/* ✅ Address */}
              <p className="print-company-line">{companyAddress || "No address"}</p>

              {/* ✅ Phone 1 · Phone 2 · Phone 3 */}
              {phoneLine && (
                <p className="print-company-line">Tel: {phoneLine}</p>
              )}

              <p className="print-company-line">
                {companyEmail ? `Email: ${companyEmail}` : ""}
                {company?.company_code ? ` · Code: ${company.company_code}` : ""}
              </p>
            </div>
          </div>
          <div className="print-header-right">
            <div className="print-meta-item">
              <span className="print-meta-label">Report Date</span>
              <span className="print-meta-value">{displayDate}</span>
            </div>
            <div className="print-meta-item">
              <span className="print-meta-label">Fiscal Year</span>
              <span className="print-meta-value">
                {fiscalYearLabel}-{fiscalYearLabel + 1}
              </span>
            </div>
            <div className="print-meta-item">
              <span className="print-meta-label">Currency</span>
              <span className="print-meta-value">{companyCurrency}</span>
            </div>
          </div>
        </div>

        <div className="print-title-block">
          <h2 className="print-report-title">BALANCE SHEET</h2>
          <p className="print-report-subtitle">As of {displayDate}</p>
        </div>

        <div className="print-bs-grid">
          <div className="print-bs-col">
            <div className="print-bs-col-header">
              <span>ASSETS</span>
              <span>Amount</span>
            </div>

            <div className="print-bs-group">
              <div className="print-bs-group-title">Current Assets</div>
              {currentAssets.length === 0 ? (
                <div className="print-bs-empty">—</div>
              ) : (
                currentAssets.map((item, i) => (
                  <div className="print-bs-row" key={`pa-${i}`}>
                    <span>{item.account_name}</span>
                    <span>{signedAmount(item.amount)}</span>
                  </div>
                ))
              )}
              <div className="print-bs-subtotal">
                <span>Total Current Assets</span>
                <span>{formatAmount(sectionTotal(currentAssets))}</span>
              </div>
            </div>

            <div className="print-bs-group">
              <div className="print-bs-group-title">Non-Current Assets</div>
              {nonCurrentAssets.length === 0 ? (
                <div className="print-bs-empty">—</div>
              ) : (
                nonCurrentAssets.map((item, i) => (
                  <div className="print-bs-row" key={`pn-${i}`}>
                    <span>{item.account_name}</span>
                    <span>{signedAmount(item.amount)}</span>
                  </div>
                ))
              )}
              <div className="print-bs-subtotal">
                <span>Total Non-Current Assets</span>
                <span>{formatAmount(sectionTotal(nonCurrentAssets))}</span>
              </div>
            </div>

            {supplierAdvances.length > 0 && (
              <div className="print-bs-group">
                <div className="print-bs-group-title">Other Current Assets — Supplier Advances</div>
                {supplierAdvances.map((item, i) => (
                  <div className="print-bs-row" key={`psa-${i}`}>
                    <span>{item.account_name}</span>
                    <span>{signedAmount(item.amount)}</span>
                  </div>
                ))}
                <div className="print-bs-subtotal">
                  <span>Total Other Current Assets</span>
                  <span>{formatAmount(sectionTotal(supplierAdvances))}</span>
                </div>
              </div>
            )}

            <div className="print-bs-total">
              <span>TOTAL ASSETS</span>
              <span>{formatAmount(totalAssets)}</span>
            </div>
          </div>

          <div className="print-bs-col">
            <div className="print-bs-col-header">
              <span>LIABILITIES &amp; EQUITY</span>
              <span>Amount</span>
            </div>

            <div className="print-bs-group">
              <div className="print-bs-group-title">Current Liabilities</div>
              {currentLiabilities.length === 0 ? (
                <div className="print-bs-empty">—</div>
              ) : (
                currentLiabilities.map((item, i) => (
                  <div className="print-bs-row" key={`pl-${i}`}>
                    <span>{item.account_name}</span>
                    <span>{signedAmount(item.amount)}</span>
                  </div>
                ))
              )}
              <div className="print-bs-subtotal">
                <span>Total Current Liabilities</span>
                <span>{formatAmount(sectionTotal(currentLiabilities))}</span>
              </div>
            </div>

            <div className="print-bs-group">
              <div className="print-bs-group-title">Non-Current Liabilities</div>
              {nonCurrentLiabilities.length === 0 ? (
                <div className="print-bs-empty">—</div>
              ) : (
                nonCurrentLiabilities.map((item, i) => (
                  <div className="print-bs-row" key={`pnl-${i}`}>
                    <span>{item.account_name}</span>
                    <span>{signedAmount(item.amount)}</span>
                  </div>
                ))
              )}
              <div className="print-bs-subtotal">
                <span>Total Non-Current Liabilities</span>
                <span>{formatAmount(sectionTotal(nonCurrentLiabilities))}</span>
              </div>
            </div>

            <div className="print-bs-group">
              <div className="print-bs-group-title">Equity</div>
              {equityAccounts.length === 0 ? (
                <div className="print-bs-empty">—</div>
              ) : (
                equityAccounts.map((item, i) => (
                  <div className="print-bs-row" key={`pe-${i}`}>
                    <span>{item.account_name}</span>
                    <span>{signedAmount(item.amount)}</span>
                  </div>
                ))
              )}

              <div className="print-bs-row print-bs-row-highlight">
                <span>Current Period Profit / (Loss)</span>
                <span>{signedAmount(currentProfitLoss)}</span>
              </div>

              <div className="print-bs-subtotal">
                <span>Total Equity</span>
                <span>{formatAmount(totalEquity)}</span>
              </div>
            </div>

            <div className="print-bs-total">
              <span>TOTAL LIABILITIES &amp; EQUITY</span>
              <span>{formatAmount(liabilitiesAndEquity)}</span>
            </div>
          </div>
        </div>

        <div className="print-status">
          {balanced ? (
            <p className="print-status-ok">
              ✓ Assets = Liabilities + Equity — Balance sheet is balanced.
            </p>
          ) : (
            <p className="print-status-bad">
              ! Out of balance by {formatAmount(difference)}.
            </p>
          )}
        </div>

        <div className="print-footer">
          <div className="print-footer-row">
            <div className="print-signature">
              <div className="print-signature-line" />
              <span>Prepared By</span>
            </div>
            <div className="print-signature">
              <div className="print-signature-line" />
              <span>Checked By</span>
            </div>
            <div className="print-signature">
              <div className="print-signature-line" />
              <span>Approved By</span>
            </div>
          </div>
          <p className="print-footer-note">
            Generated on {new Date().toLocaleString()} · HantiZone IMS
          </p>
        </div>
      </div>

      <style>{`
        .print-only { display: none; }
        @page { size: A4 portrait; margin: 12mm 14mm 14mm 14mm; }
        @media print {
          html, body { background: #fff !important; color: #000 !important; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; font-size: 10pt; }
          body * { visibility: hidden !important; }
          .print-only, .print-only * { visibility: visible !important; }
          .print-only { position: absolute !important; left: 0; top: 0; width: 100%; display: block !important; padding: 0; margin: 0; background: #fff !important; }
          .screen-only { display: none !important; }
          .print-page { font-family: "Helvetica Neue", Arial, sans-serif; color: #111; }
          .print-header { display: flex; justify-content: space-between; align-items: flex-start; gap: 10mm; padding-bottom: 4mm; border-bottom: 1.5pt solid #222; margin-bottom: 4mm; }
          .print-header-left { display: flex; align-items: center; gap: 6mm; flex: 1; }
          .print-logo { max-height: 18mm; max-width: 26mm; object-fit: contain; }
          .print-company-name { font-size: 14pt; font-weight: 700; color: #170f48; margin: 0 0 1mm 0; }
          .print-company-line { font-size: 8.5pt; color: #444; margin: 0.4mm 0; }
          .print-header-right { text-align: right; font-size: 8.5pt; }
          .print-meta-item { display: flex; justify-content: flex-end; gap: 4mm; margin-bottom: 1mm; }
          .print-meta-label { color: #666; }
          .print-meta-value { font-weight: 700; min-width: 26mm; text-align: right; color: #111; }
          .print-title-block { text-align: center; margin-bottom: 5mm; }
          .print-report-title { font-size: 14pt; font-weight: 700; letter-spacing: 2pt; color: #170f48; margin: 0 0 1mm 0; }
          .print-report-subtitle { font-size: 9.5pt; color: #555; margin: 0; }
          .print-bs-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8mm; }
          .print-bs-col { display: flex; flex-direction: column; page-break-inside: avoid; }
          .print-bs-col-header { display: flex; justify-content: space-between; padding: 3mm; background: #f0f2f5 !important; border: 0.5pt solid #888; font-size: 9.5pt; font-weight: 700; color: #170f48; text-transform: uppercase; letter-spacing: 0.4pt; }
          .print-bs-group { margin-top: 3mm; border: 0.5pt solid #bbb; }
          .print-bs-group-title { padding: 2mm 3mm; background: #f8fafc !important; font-size: 9pt; font-weight: 700; color: #170f48; border-bottom: 0.5pt solid #bbb; text-transform: uppercase; letter-spacing: 0.3pt; }
          .print-bs-row { display: flex; justify-content: space-between; padding: 1.6mm 3mm; border-bottom: 0.3pt solid #e0e4ea; font-size: 9pt; }
          .print-bs-row:last-child { border-bottom: none; }
          .print-bs-row span:last-child { font-variant-numeric: tabular-nums; white-space: nowrap; text-align: right; }
          .print-bs-row-highlight { background: #fffbea !important; font-weight: 700; }
          .print-bs-empty { padding: 2mm 3mm; font-size: 8.5pt; color: #999; font-style: italic; }
          .print-bs-subtotal { display: flex; justify-content: space-between; padding: 2mm 3mm; background: #f1f5f9 !important; font-weight: 700; font-size: 9pt; border-top: 0.5pt solid #999; }
          .print-bs-subtotal span:last-child { font-variant-numeric: tabular-nums; white-space: nowrap; }
          .print-bs-total { display: flex; justify-content: space-between; padding: 2.5mm 3mm; margin-top: 3mm; background: #170f48 !important; color: #fff !important; font-weight: 800; font-size: 10pt; letter-spacing: 0.4pt; }
          .print-bs-total span:last-child { font-variant-numeric: tabular-nums; white-space: nowrap; }
          .print-status { margin-top: 5mm; text-align: center; font-size: 9pt; }
          .print-status-ok { color: #1e7e34; font-weight: 700; margin: 0; }
          .print-status-bad { color: #c82333; font-weight: 700; margin: 0; }
          .print-footer { margin-top: 10mm; }
          .print-footer-row { display: flex; justify-content: space-between; gap: 8mm; margin-bottom: 5mm; }
          .print-signature { flex: 1; text-align: center; font-size: 8.5pt; color: #333; }
          .print-signature-line { border-top: 0.5pt solid #333; margin-bottom: 1.5mm; height: 10mm; }
          .print-footer-note { text-align: center; font-size: 7.5pt; color: #888; margin: 0; }
        }
      `}</style>
    </div>
  );
}
